import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (TOKEN) mapboxgl.accessToken = TOKEN;

const TERRITORY_DEFAULTS = {
  GP: { lat: 16.2650, lng: -61.5510, zoom: 9.5 },
  MQ: { lat: 14.6415, lng: -61.0242, zoom: 10 },
  GF: { lat: 4.0000, lng: -53.0000, zoom: 7 },
  RE: { lat: -21.1151, lng: 55.5364, zoom: 10 },
  ALL: { lat: 7.5, lng: -3.0, zoom: 1.5 }, // fallback world
};

/**
 * Carte interactive des relais LOLODRIVE (Mapbox GL).
 * Props :
 *   - points: [{id, name, code, lat, lng, city, territory, status}, ...]
 *   - territory: 'GP' | 'MQ' | 'GF' | 'RE' | null  → recadre la vue
 *   - onSelect(point): callback clic marqueur
 *   - focusCode: code d'un relais à cibler (flyTo + popup ouverte)
 *   - ratings: { [code]: { avg, count } } → note ⭐ dans les popups
 *   - height: '420px' (par défaut)
 */
export default function LoloPointsMap({ points = [], pops = [], territory = null, onSelect, focusCode = null, ratings = null, height = '460px' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(false);

  // Initial map setup. WebGL peut être indisponible sur certains appareils :
  // dans ce cas, le composant affiche une liste de relais sans casser la page.
  const initialTerritoryRef = useRef(territory);
  useEffect(() => {
    if (!TOKEN || mapRef.current || !containerRef.current) return undefined;
    const init = TERRITORY_DEFAULTS[initialTerritoryRef.current] || TERRITORY_DEFAULTS.GP;

    try {
      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: {
          version: 8,
          sources: {
            basemap: {
              type: 'raster',
              tiles: [
                'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
              ],
              tileSize: 256,
              maxzoom: 16,
              attribution: '© Esri © OpenStreetMap contributors',
            },
          },
          layers: [{ id: 'basemap-dark', type: 'raster', source: 'basemap' }],
        },
        center: [init.lng, init.lat],
        zoom: init.zoom,
        attributionControl: true,
      });
      map.on('error', (event) => {
        if (!event?.error) return;
        console.error('LOLODRIVE map error:', event.error);
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      mapRef.current = map;
    } catch (error) {
      console.error('LOLODRIVE map unavailable:', error);
      mapRef.current = null;
      setMapError(true);
      return undefined;
    }

    return () => {
      try {
        mapRef.current?.remove();
      } catch (error) {
        console.debug('Map cleanup skipped:', error);
      }
      mapRef.current = null;
    };
  }, []);

  // Recenter on territory change
  useEffect(() => {
    if (!mapRef.current) return;
    const conf = TERRITORY_DEFAULTS[territory] || TERRITORY_DEFAULTS.ALL;
    mapRef.current.flyTo({ center: [conf.lng, conf.lat], zoom: conf.zoom, speed: 1.4 });
  }, [territory]);

  // Update markers when points change
  useEffect(() => {
    if (!mapRef.current) return;
    // Clear previous markers
    markersRef.current.forEach((m) => m.marker.remove());
    markersRef.current = [];

    const filtered = points.filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number');
    if (filtered.length === 0 && pops.length === 0) return;

    filtered.forEach((p) => {
      const rating = ratings?.[p.code];
      const gold = !!(rating && rating.avg >= 4.5);
      const el = document.createElement('div');
      el.className = 'kdm-lolo-marker';
      el.setAttribute('data-testid', `map-marker-${p.code}`);
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: ${gold ? 'linear-gradient(135deg, #FFD700, #D9B35A)' : 'linear-gradient(135deg, #D9B35A, #7c3aed)'};
        border: 2px solid ${gold ? '#FFD700' : 'rgba(255,255,255,0.9)'};
        box-shadow: ${gold ? '0 0 16px rgba(255,215,0,0.85)' : '0 2px 8px rgba(0,0,0,0.6)'};
        cursor: pointer; display: flex; align-items: center; justify-content: center;
        color: #000; font-weight: 700; font-size: 11px;
      `;
      el.textContent = p.territory || '•';

      const popup = new mapboxgl.Popup({ offset: 18, closeButton: false }).setHTML(`
        <div style="font-family: system-ui; min-width: 200px; padding: 4px 0;">
          <div style="font-weight: 700; font-size: 14px; color: #0f172a;">${escapeHtml(p.name)}</div>
          ${rating ? `<div data-testid="popup-rating-${escapeHtml(p.code)}" style="font-size: 12px; color: #b45309; font-weight: 700; margin-top: 2px;">★ ${escapeHtml(String(rating.avg))} <span style="color:#94a3b8; font-weight:400;">(${escapeHtml(String(rating.count))} avis)</span></div>` : ''}
          ${gold ? `<div data-testid="popup-gold-${escapeHtml(p.code)}" style="display:inline-block;background:linear-gradient(135deg,#FFD700,#D9B35A);color:#1F0A33;font-size:10px;font-weight:800;padding:2px 10px;border-radius:999px;margin-top:4px;letter-spacing:0.5px;">🏆 RELAIS D'OR</div>` : ''}
          <div style="font-size: 11px; color: #64748b; margin: 4px 0; font-family: monospace;">${escapeHtml(p.code)} · ${escapeHtml(p.territory || '?')}</div>
          <div style="font-size: 12px; color: #1e293b;">${escapeHtml(p.address || '')}</div>
          <div style="font-size: 12px; color: #1e293b;">${escapeHtml(p.city || '')}</div>
          ${p.zone_name ? `<div style="font-size: 11px; color: #64748b; margin-top: 4px;">Zone: ${escapeHtml(p.zone_name)}</div>` : ''}
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el }).setLngLat([p.lng, p.lat]).setPopup(popup).addTo(mapRef.current);
      el.addEventListener('click', () => onSelect?.(p));
      markersRef.current.push({ code: p.code, marker });
    });

    // Marqueurs POP'S — boutiques détaillantes (vendeurs éphémères COOP'ACT)
    const popsFiltered = pops.filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number');
    popsFiltered.forEach((s) => {
      const el = document.createElement('div');
      el.className = 'kdm-pops-marker';
      el.setAttribute('data-testid', `map-pops-marker-${s.user_id}`);
      el.style.cssText = `
        min-width: 30px; height: 24px; padding: 0 6px; border-radius: 8px;
        background: linear-gradient(135deg, #10b981, #047857);
        border: 2px solid rgba(255,255,255,0.9); box-shadow: 0 2px 8px rgba(0,0,0,0.6);
        cursor: pointer; display: flex; align-items: center; justify-content: center;
        color: #fff; font-weight: 800; font-size: 9px; letter-spacing: 0.5px;
      `;
      el.textContent = "POP'S";
      const popup = new mapboxgl.Popup({ offset: 18, closeButton: false }).setHTML(`
        <div style="font-family: system-ui; min-width: 200px; padding: 4px 0;">
          <div style="display:inline-block;background:linear-gradient(135deg,#10b981,#047857);color:#fff;font-size:9px;font-weight:800;padding:2px 8px;border-radius:999px;letter-spacing:0.5px;">POP'S — VENDEUR ÉPHÉMÈRE</div>
          <div style="font-weight: 700; font-size: 14px; color: #0f172a; margin-top: 4px;">${escapeHtml(s.company_name || '')}</div>
          ${s.rating_avg ? `<div style="font-size: 12px; color: #b45309; font-weight: 700;">★ ${escapeHtml(String(s.rating_avg))} <span style="color:#94a3b8;font-weight:400;">(${escapeHtml(String(s.rating_count || 0))} avis)</span></div>` : ''}
          <div style="font-size: 12px; color: #1e293b; margin-top: 2px;">${escapeHtml(s.locality || '')} ${s.country_code ? `· ${escapeHtml(s.country_code)}` : ''}</div>
          <div style="font-size: 10px; color: #64748b; margin-top: 4px;">Partenaire d'Offres de Produits Solidaires — lots en salle COOP'ACT</div>
        </div>
      `);
      const marker = new mapboxgl.Marker({ element: el }).setLngLat([s.lng, s.lat]).setPopup(popup).addTo(mapRef.current);
      markersRef.current.push({ code: `pops-${s.user_id}`, marker });
    });

    // Focus direct sur un relais ciblé (icône localisation de l'espace PASS)
    const focused = focusCode ? filtered.find((p) => p.code === focusCode) : null;
    if (focused) {
      mapRef.current.flyTo({ center: [focused.lng, focused.lat], zoom: 13, speed: 1.6 });
      const entry = markersRef.current.find((m) => m.code === focusCode);
      if (entry && !entry.marker.getPopup().isOpen()) entry.marker.togglePopup();
      onSelect?.(focused);
      return;
    }

    // Auto-fit bounds when no territory filter or several points
    if (!territory && filtered.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      filtered.forEach((p) => bounds.extend([p.lng, p.lat]));
      mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 8, duration: 800 });
    }
  }, [points, pops, territory, onSelect, focusCode, ratings]);

  if (!TOKEN || mapError) {
    const visiblePoints = points.filter((point) => point?.name).slice(0, 8);
    return (
      <div
        data-testid="map-fallback"
        className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-white/80"
      >
        <p className="text-center font-semibold text-white">La carte interactive n’est pas disponible sur cet appareil.</p>
        <p className="mt-2 text-center text-sm text-white/60">Vous pouvez tout de même sélectionner un relais dans la liste.</p>
        {visiblePoints.length > 0 ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {visiblePoints.map((point) => (
              <button
                key={point.id || point.code}
                type="button"
                onClick={() => onSelect?.(point)}
                className="rounded-lg border border-white/10 bg-white/[0.04] p-3 text-left transition-colors hover:bg-white/[0.08]"
              >
                <span className="block font-semibold text-white">{point.name}</span>
                <span className="mt-1 block text-xs text-white/60">{[point.city, point.territory].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-5 text-center text-sm text-white/60">Les relais seront affichés dès qu’ils seront disponibles.</p>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="lolo-points-map"
      className="rounded-xl overflow-hidden border border-white/10"
      style={{ width: '100%', height }}
    />
  );
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
